import React from 'react';
import { Link } from 'react-router-dom';
import { 
  GitBranch, ArrowRight, CheckCircle, Target, Users, UserCheck,
  GraduationCap, MessageSquare, TrendingUp, FileText, BarChart3, Shield
} from 'lucide-react';

const BPCMSection = () => {
  const levers = [
    { name: 'Management & Planning', icon: Target, color: 'from-blue-500 to-blue-600' },
    { name: 'Leadership Engagement', icon: Users, color: 'from-purple-500 to-purple-600' },
    { name: 'Project Team Effectiveness', icon: UserCheck, color: 'from-emerald-500 to-emerald-600' },
    { name: 'Business Process Alignment', icon: GitBranch, color: 'from-amber-500 to-amber-600' },
    { name: 'Learning & Capability', icon: GraduationCap, color: 'from-pink-500 to-pink-600' },
    { name: 'Communications', icon: MessageSquare, color: 'from-cyan-500 to-cyan-600' },
  ];

  const features = [
    '7 Change Management Levers',
    'Industry Benchmarking',
    '12-Month Roadmap',
    'ROI Analysis',
    'Risk Mitigation',
    'Case Studies'
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-slate-900 via-blue-950 to-purple-950 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-full px-4 py-1.5 mb-6">
            <GitBranch className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-medium text-blue-300">NEW: BPCM Assessment</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Business Process Change Management
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Assess your organization&apos;s change management capabilities across the 7 key levers 
            that drive successful business transformation.
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Levers */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-400" />
              7 Change Management Levers
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {levers.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={idx}
                    className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 hover:bg-white/10 hover:border-blue-500/30 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 bg-gradient-to-br ${item.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-medium text-white text-sm">{item.name}</h4>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Additional info */}
            <div className="mt-6 p-4 bg-blue-900/30 border border-blue-700/30 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-blue-300">Realization & Sustainability</span>
              </div>
              <p className="text-xs text-gray-400">
                Build capabilities to sustain change beyond implementation with structured knowledge transfer and benefits tracking.
              </p>
            </div>
          </div>

          {/* Right side - Features & CTA */}
          <div className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-400" />
              What You&apos;ll Get
            </h3>
            
            <div className="grid grid-cols-2 gap-3 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span className="text-sm text-gray-300">{feature}</span>
                </div>
              ))}
            </div>

            {/* Report Preview */}
            <div className="bg-black/20 rounded-xl p-4 mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">C-suite Ready Insight Report</p>
                  <p className="text-sm text-white">Executive summary, roadmap, areas of concern</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full">Executive Summary</span>
                <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full">Industry Comparison</span>
                <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded-full">ROI Analysis</span>
              </div>
            </div>

            {/* CTA Button */}
            <Link 
              to="/bpcm-assessment"
              className="group flex items-center justify-center gap-3 w-full bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 hover:from-blue-600 hover:via-purple-600 hover:to-indigo-600 text-white font-semibold py-4 px-6 rounded-xl transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30"
              data-testid="launch-bpcm"
            >
              <GitBranch className="w-5 h-5" />
              <span>Start BPCM Assessment</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <p className="text-center text-xs text-gray-500 mt-3">
              Takes ~10-15 minutes • AI-powered analysis • PowerPoint export
            </p>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '7', label: 'Change Levers' },
            { value: '5', label: 'Maturity Levels' },
            { value: '21', label: 'Assessment Questions' },
            { value: '15+', label: 'Industries' }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-sm text-gray-500 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BPCMSection;
